// Firma del contrato en un solo modal: abrir el contrato, aceptar, mandar el código y firmar.
// El estado que debe sobrevivir al cerrar (aceptación, contrato abierto, código enviado) vive en SignStep.
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Check, ExternalLink, FileText } from 'lucide-react';
import { ApiError } from '@/api/errors';
import { Button, Modal } from '@/kit';
import { useContractDocument, useSignatureSendOtp, useSignatureSign } from '../api/useWizard';
import { wizardErrorMsg } from '../api/wizardError';
import { isFinancedImeiNotPinned } from '../financedImeiErrors';
import { integrationFailure, type IntegrationFailure } from '../integrationErrors';
import { formatCountdown, maskedPhone, otpSentMessage, resendWaitSeconds } from '../otpResend';

const HINT = 'text-[11.5px] leading-[1.35] text-muted-foreground';

const OTP_ERROR_MESSAGE: Partial<Record<string, string>> = {
    OTP_INVALID: 'Código incorrecto — revísalo e intenta de nuevo.',
    OTP_EXPIRED: 'El código venció — reenvíalo.',
    OTP_MAX_TRIES: 'Se agotaron los intentos — reenvía un código nuevo.',
};

export interface ContractSignModalProps {
    sessionId: string;
    customerName: string | undefined;
    phone: string | undefined;
    /** Contrato ya firmado: el modal solo deja abrir el documento. */
    signed: boolean;
    open: boolean;
    onClose: () => void;
    onSigned: () => void;
    accepted: boolean;
    onAcceptedChange: (accepted: boolean) => void;
    /** El vendedor ya abrió el contrato en otra pestaña; habilita el envío del código. */
    documentOpened: boolean;
    onDocumentOpened: () => void;
    /** Destino enmascarado del código; presente = el código ya salió. */
    masked: string | null;
    onCodeSent: (masked: string) => void;
    /** Segundos para poder reenviar. */
    resendIn: number;
    onResendWait: (seconds: number) => void;
    onIntegrationFailure: (failure: IntegrationFailure) => void;
    onImeiNotPinned: () => void;
}

export function ContractSignModal({
    sessionId,
    customerName,
    phone,
    signed,
    open,
    onClose,
    onSigned,
    accepted,
    onAcceptedChange,
    documentOpened,
    onDocumentOpened,
    masked,
    onCodeSent,
    resendIn,
    onResendWait,
    onIntegrationFailure,
    onImeiNotPinned,
}: ContractSignModalProps) {
    const sendOtp = useSignatureSendOtp(sessionId);
    const sign = useSignatureSign(sessionId);
    const contractDocument = useContractDocument(sessionId);

    const [code, setCode] = useState('');
    const [docError, setDocError] = useState<string | null>(null);
    // Evita un segundo envío por doble clic antes de que el botón se deshabilite.
    const sending = useRef(false);

    const close = (): void => {
        setDocError(null);
        setCode('');
        onClose();
    };

    /** Abre el PDF en una pestaña nueva; se abre en el clic para que el navegador no la bloquee. */
    const openDocument = async (): Promise<void> => {
        setDocError(null);
        // Sin `noopener`: con esa bandera `window.open` devuelve `null` aunque haya abierto.
        const tab = window.open('', '_blank');
        if (!tab) {
            setDocError(
                'Tu navegador bloqueó la ventana. Permite las ventanas emergentes de este sitio e intenta de nuevo.',
            );
            return;
        }
        tab.opener = null;
        tab.document.title = 'Contrato de Crédito SureKeep';
        tab.document.body.textContent = 'Generando el contrato…';
        try {
            const link = await contractDocument.mutateAsync();
            tab.location.href = link.url;
            onDocumentOpened();
        } catch (e) {
            tab.close();
            if (e instanceof ApiError && e.code === 'CONTRACT_DETAILS_INCOMPLETE') {
                setDocError('Faltan datos del cliente o del equipo para armar el contrato.');
                return;
            }
            setDocError(wizardErrorMsg(e, 'No se pudo abrir el contrato — intenta de nuevo.'));
        }
    };

    const sendCode = async (): Promise<void> => {
        if (sending.current || resendIn > 0) return;
        sending.current = true;
        try {
            const r = await sendOtp.mutateAsync();
            // Sin `otpSent` el SMS no salió: no se anuncia ni corre la espera.
            if (!r.otpSent) {
                toast.error('No salió el código. Vuelve a mandarlo.');
                return;
            }
            const destination = maskedPhone(phone) ?? r.otpDestinationMasked;
            setCode('');
            onCodeSent(destination);
            toast.success(otpSentMessage(destination));
        } catch (e) {
            if (isFinancedImeiNotPinned(e)) {
                close();
                onImeiNotPinned();
                return;
            }
            const integration = integrationFailure(e);
            if (integration) {
                close();
                onIntegrationFailure(integration);
                return;
            }
            const wait = resendWaitSeconds(e);
            if (wait !== undefined) {
                onResendWait(wait);
                toast.error(`Espera ${formatCountdown(wait)} para reenviar el código.`);
                return;
            }
            toast.error(wizardErrorMsg(e, 'No se pudo enviar el código — intenta de nuevo.'));
        } finally {
            sending.current = false;
        }
    };

    const signContract = async (): Promise<void> => {
        try {
            await sign.mutateAsync(code);
            toast.success('Contrato firmado.');
            close();
            onSigned();
        } catch (e) {
            const integration = integrationFailure(e);
            if (integration) {
                close();
                onIntegrationFailure(integration);
                return;
            }
            const otpMessage = e instanceof ApiError ? OTP_ERROR_MESSAGE[e.code] : undefined;
            if (otpMessage !== undefined) {
                toast.error(otpMessage);
                if (e instanceof ApiError && e.code === 'OTP_MAX_TRIES') setCode('');
                return;
            }
            toast.error(wizardErrorMsg(e, 'No se pudo firmar el contrato — intenta de nuevo.'));
        }
    };

    const canSend = accepted && (documentOpened || masked !== null);

    return (
        <Modal
            isOpen={open}
            onClose={close}
            size="xl"
            className="gap-0 p-0 sm:p-0"
            title={
                <span className="block px-4 pt-3">
                    <span className="block text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                        Firma del contrato
                    </span>
                    <span className="block text-[16px] font-bold leading-tight">
                        {customerName ?? 'Acreditado'}
                    </span>
                </span>
            }
            titleAction={
                signed ? (
                    <span className="mr-12 mt-3 rounded-full border border-success/40 bg-success/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em] text-success">
                        Firmado
                    </span>
                ) : (
                    <span className="mr-12 mt-3 rounded-full border border-warning/40 bg-warning-soft px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em] text-warning">
                        Borrador
                    </span>
                )
            }
        >
            <div className="mt-3 border-y border-border bg-muted/20 px-4 py-8">
                <div className="flex flex-col items-center gap-3 text-center">
                    <span className="flex size-10 items-center justify-center rounded-[10px] bg-info-soft text-info">
                        <FileText className="size-5" aria-hidden />
                    </span>
                    {docError !== null ? (
                        <>
                            <p role="alert" className="text-[13px] text-muted-foreground">
                                {docError}
                            </p>
                            <Button
                                size="sm"
                                isLoading={contractDocument.isPending}
                                onClick={() => void openDocument()}
                            >
                                Reintentar
                            </Button>
                        </>
                    ) : (
                        <>
                            <p className="text-[13px] text-muted-foreground">
                                {documentOpened
                                    ? 'El contrato se abrió en una pestaña nueva.'
                                    : 'El contrato se abre en una pestaña nueva para que el cliente lo lea.'}
                            </p>
                            <Button
                                size="sm"
                                variant={documentOpened || signed ? 'outline' : 'default'}
                                isLoading={contractDocument.isPending}
                                rightIcon={<ExternalLink className="size-3.5" aria-hidden />}
                                onClick={() => void openDocument()}
                            >
                                {documentOpened ? 'Abrir otra vez' : 'Abrir contrato'}
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {signed ? (
                <div className="flex items-center justify-between gap-3 bg-success/5 p-4">
                    <div className="flex items-center gap-2 text-[13px]">
                        <Check className="size-4 text-success" strokeWidth={3} aria-hidden />
                        <span className="font-semibold">Contrato firmado — solo lectura.</span>
                    </div>
                    <Button size="sm" variant="outline" onClick={close}>
                        Cerrar
                    </Button>
                </div>
            ) : (
                <div className="flex flex-col gap-3 p-4">
                    <label className="flex cursor-pointer items-start gap-2 text-[13px]">
                        <input
                            type="checkbox"
                            checked={accepted}
                            onChange={(e) => {
                                onAcceptedChange(e.target.checked);
                            }}
                            className="mt-0.5 size-4 flex-none accent-primary"
                        />
                        <span>
                            El cliente leyó el contrato y <strong>acepta los términos</strong>.
                        </span>
                    </label>
                    {masked === null ? (
                        <div className="flex flex-wrap items-center justify-end gap-3">
                            <span className={HINT}>
                                {!documentOpened
                                    ? 'Abre el contrato para habilitar el envío.'
                                    : accepted
                                      ? 'Mandaremos un código al celular del cliente.'
                                      : 'Marca la aceptación para habilitar el envío.'}
                            </span>
                            <Button
                                size="sm"
                                disabled={!canSend || resendIn > 0}
                                isLoading={sendOtp.isPending}
                                onClick={() => void sendCode()}
                            >
                                {resendIn > 0
                                    ? `Reenviar en ${formatCountdown(resendIn)}`
                                    : 'Enviar código al cliente'}
                            </Button>
                        </div>
                    ) : (
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className={`min-w-0 flex-1 ${HINT}`}>
                                {otpSentMessage(masked)} Si lo reenvías, usa el último que llegue.{' '}
                                {resendIn > 0 ? (
                                    <span>Reenvía en {formatCountdown(resendIn)}.</span>
                                ) : (
                                    <button
                                        type="button"
                                        disabled={sendOtp.isPending}
                                        onClick={() => void sendCode()}
                                        className="font-semibold text-primary underline underline-offset-2"
                                    >
                                        Reenviar código
                                    </button>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    maxLength={4}
                                    placeholder="0000"
                                    aria-label="Código de firma (4 dígitos)"
                                    value={code}
                                    onChange={(e) => {
                                        setCode(e.target.value.replace(/\D/g, '').slice(0, 4));
                                    }}
                                    className="h-10 w-[120px] rounded-md border border-input bg-background text-center font-mono text-[18px] font-bold tracking-[0.3em] tabular-nums outline-none focus:border-ring"
                                />
                                <Button
                                    disabled={code.length !== 4}
                                    isLoading={sign.isPending}
                                    rightIcon={<Check className="size-4" aria-hidden />}
                                    onClick={() => void signContract()}
                                >
                                    Firmar
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </Modal>
    );
}
